import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  MessageCircle,
  Send,
  Search,
  MoreVertical,
  Phone,
  Video,
  Paperclip,
  Smile,
  CheckCheck,
  QrCode,
  RefreshCw,
  User
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const WhatsApp = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    checkConnection();
    // Simulate loading conversations
    setTimeout(() => {
      loadDemoConversations();
      setLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const checkConnection = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/status`);
      setConnected(response.data.connected || false);
    } catch (error) {
      setConnected(false);
    }
  };

  const loadDemoConversations = () => {
    // Demo data - em produção viria do backend
    const demoConversations = [
      {
        id: '1',
        name: 'Cybercoop - Cliente',
        phone: '+55 11 98765-4321',
        lastMessage: 'Quando posso retirar o pedido?',
        timestamp: new Date(Date.now() - 300000),
        unread: 2,
        avatar: null
      },
      {
        id: '2',
        name: 'Carlos - Vendedor',
        phone: '+55 11 91234-5678',
        lastMessage: 'Preciso confirmar um pedido',
        timestamp: new Date(Date.now() - 3600000),
        unread: 0,
        avatar: null
      },
      {
        id: '3',
        name: 'Joao - Cliente',
        phone: '+55 11 99999-8888',
        lastMessage: 'Qual o valor do frete?',
        timestamp: new Date(Date.now() - 86400000),
        unread: 1,
        avatar: null
      }
    ];
    setConversations(demoConversations);
  };

  const selectConversation = (conversation) => {
    setSelectedConversation(conversation);
    loadMessages(conversation.id);
    // Marcar como lido
    setConversations(conversations.map(c => 
      c.id === conversation.id ? { ...c, unread: 0 } : c
    ));
  };

  const loadMessages = (conversationId) => {
    // Demo messages - em produção viria do backend
    const demoMessages = {
      '1': [
        { id: '1', text: 'Olá! Fiz um pedido ontem', sender: 'them', timestamp: new Date(Date.now() - 7200000) },
        { id: '2', text: 'Olá! Seu pedido já está pronto para retirada', sender: 'me', timestamp: new Date(Date.now() - 3600000) },
        { id: '3', text: 'Quando posso retirar o pedido?', sender: 'them', timestamp: new Date(Date.now() - 300000) }
      ],
      '2': [
        { id: '1', text: 'Preciso confirmar um pedido', sender: 'them', timestamp: new Date(Date.now() - 3600000) },
        { id: '2', text: 'Pode me passar os detalhes?', sender: 'me', timestamp: new Date(Date.now() - 3000000) }
      ],
      '3': [
        { id: '1', text: 'Qual o valor do frete?', sender: 'them', timestamp: new Date(Date.now() - 86400000) }
      ]
    };
    setMessages(demoMessages[conversationId] || []);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    const message = {
      id: Date.now().toString(),
      text: newMessage,
      sender: 'me',
      timestamp: new Date()
    };

    setMessages([...messages, message]);
    setNewMessage('');

    // Atualizar última mensagem da conversa
    setConversations(conversations.map(c => 
      c.id === selectedConversation.id 
        ? { ...c, lastMessage: newMessage, timestamp: new Date() }
        : c
    ));

    // Simular envio para o backend
    try {
      await axios.post(`${API}/whatsapp/send`, {
        conversation_id: selectedConversation.id,
        message: newMessage
      });
      toast.success('Mensagem enviada!');
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast.error('Erro ao enviar mensagem');
    }
  };

  const handleConnect = () => {
    setShowQRCode(true);
    toast.info('Escaneie o QR Code com seu WhatsApp');
    
    // Simular conexão após 3 segundos
    setTimeout(() => {
      setConnected(true);
      setShowQRCode(false);
      toast.success('WhatsApp conectado com sucesso!');
      loadDemoConversations();
    }, 3000);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return formatTime(date);
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ontem';
    } else {
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <MessageCircle className="w-16 h-16 mx-auto mb-4 text-green-600 animate-pulse" />
          <p className="text-muted-foreground">Carregando WhatsApp...</p>
        </div>
      </div>
    );
  }

  if (!connected && !showQRCode) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="stat-card max-w-md text-center">
          <div className="bg-gradient-to-br from-green-500 to-green-600 p-4 rounded-full inline-block mb-4">
            <MessageCircle className="w-16 h-16 text-white" />
          </div>
          <h2 className="text-2xl font-heading font-bold mb-2">WhatsApp Web</h2>
          <p className="text-muted-foreground mb-6">
            Conecte seu WhatsApp para gerenciar conversas, enviar mensagens e integrar com seu sistema de gestão.
          </p>
          <button
            onClick={handleConnect}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            <QrCode className="w-5 h-5" />
            Conectar WhatsApp
          </button>
          <p className="text-xs text-muted-foreground mt-4">
            Um QR Code será exibido para você escanear com seu celular
          </p>
        </div>
      </div>
    );
  }

  if (showQRCode) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="stat-card max-w-md text-center">
          <h2 className="text-2xl font-heading font-bold mb-4">Escaneie o QR Code</h2>
          <div className="bg-white p-8 rounded-lg inline-block mb-4">
            <div className="w-64 h-64 border-4 border-green-600 rounded-lg flex items-center justify-center">
              <div className="text-green-600">
                <QrCode className="w-32 h-32 mx-auto mb-2" />
                <p className="text-sm">Gerando QR Code...</p>
              </div>
            </div>
          </div>
          <ol className="text-left text-sm text-muted-foreground space-y-2 mb-4">
            <li>1. Abra o WhatsApp no seu celular</li>
            <li>2. Toque em Menu ou Configurações e selecione Aparelhos conectados</li>
            <li>3. Toque em Conectar um aparelho</li>
            <li>4. Aponte seu celular para esta tela para escanear o QR Code</li>
          </ol>
          <div className="flex items-center justify-center gap-2 text-green-600">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="text-sm">Aguardando conexão...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] bg-background rounded-lg overflow-hidden border border-border">
      {/* Sidebar - Lista de Conversas */}
      <div className="w-full md:w-1/3 border-r border-border flex flex-col bg-card">
        {/* Header da Sidebar */}
        <div className="p-4 border-b border-border bg-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-600" />
              Conversas
            </h3>
            <button className="p-2 hover:bg-muted rounded-lg transition-colors">
              <MoreVertical className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar conversa..."
              className="w-full pl-10 pr-4 py-2 bg-muted border-none rounded-lg text-sm focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        {/* Lista de Conversas */}
        <div className="flex-1 overflow-y-auto">
          {conversations.map((conversation) => (
            <button
              key={conversation.id}
              onClick={() => selectConversation(conversation)}
              className={`w-full p-4 flex items-start gap-3 hover:bg-muted transition-colors ${
                selectedConversation?.id === conversation.id ? 'bg-muted' : ''
              }`}
            >
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center flex-shrink-0">
                <User className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1 min-w-0 text-left">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="font-semibold text-sm truncate">{conversation.name}</h4>
                  <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                    {formatDate(conversation.timestamp)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground truncate flex-1">
                    {conversation.lastMessage}
                  </p>
                  {conversation.unread > 0 && (
                    <span className="bg-green-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 ml-2">
                      {conversation.unread}
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Área de Chat */}
      {selectedConversation ? (
        <div className="flex-1 flex flex-col bg-[#e5ddd5] dark:bg-[#0b141a]">
          {/* Header do Chat */}
          <div className="p-4 bg-card border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                <User className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold">{selectedConversation.name}</h3>
                <p className="text-xs text-muted-foreground">{selectedConversation.phone}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="p-2 hover:bg-muted rounded-lg transition-colors">
                <Video className="w-5 h-5 text-muted-foreground" />
              </button>
              <button className="p-2 hover:bg-muted rounded-lg transition-colors">
                <Phone className="w-5 h-5 text-muted-foreground" />
              </button>
              <button className="p-2 hover:bg-muted rounded-lg transition-colors">
                <MoreVertical className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Mensagens */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'me' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[70%] rounded-lg px-4 py-2 ${
                    message.sender === 'me'
                      ? 'bg-[#d9fdd3] dark:bg-[#005c4b] text-foreground'
                      : 'bg-white dark:bg-[#202c33] text-foreground'
                  }`}
                >
                  <p className="text-sm">{message.text}</p>
                  <div className="flex items-center justify-end gap-1 mt-1">
                    <span className="text-xs text-muted-foreground">{formatTime(message.timestamp)}</span>
                    {message.sender === 'me' && (
                      <CheckCheck className="w-4 h-4 text-blue-500" />
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input de Mensagem */}
          <div className="p-4 bg-card border-t border-border">
            <div className="flex items-center gap-2">
              <button className="p-2 hover:bg-muted rounded-lg transition-colors">
                <Smile className="w-5 h-5 text-muted-foreground" />
              </button>
              <button className="p-2 hover:bg-muted rounded-lg transition-colors">
                <Paperclip className="w-5 h-5 text-muted-foreground" />
              </button>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Digite uma mensagem"
                className="flex-1 px-4 py-2 bg-muted border-none rounded-lg focus:ring-2 focus:ring-primary"
              />
              <button
                onClick={sendMessage}
                disabled={!newMessage.trim()}
                className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-muted disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                <Send className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center bg-[#e5ddd5] dark:bg-[#0b141a]">
          <div className="text-center">
            <MessageCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Selecione uma conversa para começar</p>
          </div>
        </div>
      )}
    </div>
  );
};
